<script lang="ts" setup>
  import type { Delivery } from "~/entities/Delivery";
  import { getDeliveryStatus, type DeliveryStatus } from "~/interfaces/IDeliveryRepository";

  const localProps = defineProps<{
    delivery: Delivery;
  }>();

  const emit = defineEmits<{
    (e: "onAction", action: { text: string; value: string }): void;
  }>();

  const isExpanded = ref(false);

  const actionItems = () => [
    { text: "送信内容を見る", value: "view-item", disabled: false, hide: localProps.delivery.status === "DRAFT" },
    {
      text: "変更する",
      value: "edit-item",
      disabled: localProps.delivery.status === "SENT",
      hide: localProps.delivery.status === "DRAFT",
    },
    { text: "複製する", value: "copy-item", disabled: false, hide: false },

    {
      text: localProps.delivery.status === "STOP" ? "稼働する" : "停止する",
      value: "status-item",
      disabled: localProps.delivery.status === "SENT",
      hide: localProps.delivery.status === "DRAFT",
    },
    { text: "下書き編集する", value: "edit-item", disabled: false, hide: localProps.delivery.status !== "DRAFT" },
    { text: "削除する", value: "delete-item", disabled: false, hide: false },
  ];

  const color: Record<string, string> = {
    RUNNING: "white",
    STOP: "red",
    DRAFT: "blue",
  };

  const handleAction = (action: { text: string; value: string }) => {
    emit("onAction", action);
  };

  const getSteps = () => {
    return (localProps.delivery.steps || []).sort((a, b) => a.stepOrder - b.stepOrder);
  };
</script>
<template>
  <v-sheet class="my-4" data-testid="plan-step-delivery-card">
    <v-sheet class="header-content px-2 py-4" color="monochrome-05" rounded @click="isExpanded = !isExpanded">
      <div class="d-flex align-center">
        <div class="mr-2">
          <v-icon class="triangle" :class="{ down: isExpanded }" icon="$mdiTriangle" :size="18" />
        </div>
        <div class="flex-grow-1">
          {{ delivery.name }}
        </div>
        <div>
          <v-chip variant="outlined" :color="color[delivery.status] || ''" size="small">
            {{ getDeliveryStatus(delivery.status as DeliveryStatus, "STEP") }}
          </v-chip>
        </div>
        <div class="ml-2">
          <v-menu scroll-strategy="close">
            <template #activator="{ props }">
              <v-btn v-bind="props" icon density="compact">
                <v-icon icon="$mdiDotsVertical" />
              </v-btn>
            </template>
            <AtomsMenuPopup :list="actionItems().filter(item => !item.hide)" @update:selected="handleAction" />
          </v-menu>
        </div>
      </div>
    </v-sheet>
    <v-expand-transition>
      <div v-show="isExpanded" class="step-content py-4">
        <div v-for="(step, index) in getSteps()" :key="index">
          <!-- first item -->
          <div v-if="index == 0" class="step-list-wrapper">
            <div class="font-weight-bold mb-1">STEP {{ index + 1 }}</div>
            <div class="step-list">
              <div class="d-flex ga-4">
                <div>
                  <div class="step-list-text">ステップ名</div>
                  <div class="step-list-value">
                    {{ step.stepName }}
                  </div>
                </div>
              </div>

              <div>
                <div class="step-list-text">トリガー</div>
                <div class="step-list-value d-flex justify-center flex-wrap ga-2">
                  <v-chip
                    v-for="trigger in delivery.stepTrigger"
                    :key="trigger.userTagKey1"
                    density="compact"
                    variant="outlined"
                    color="white"
                    size="small"
                  >
                    {{ trigger.userTagName }}
                  </v-chip>
                </div>
              </div>

              <div class="d-flex ga-4">
                <div>
                  <div class="step-list-text">タイミング</div>
                  <div class="step-list-value">
                    <span v-if="step.isInstantDeliveryStep"> 即時 </span>
                    <span v-else> {{ step.deliveryDaysLater }}日後 {{ step.deliveryTime }}:00 </span>
                  </div>
                </div>

                <div>
                  <div class="step-list-text">メール配信数</div>
                  <div class="step-list-value">
                    {{ numberFormat(step.sendCount) }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- second item -->
          <div v-else class="step-list-wrapper">
            <div class="font-weight-bold mb-1">STEP {{ index + 1 }}</div>
            <div class="step-list">
              <div>
                <div class="step-list-text">ステップ名</div>
                <div class="step-list-value">
                  {{ step.stepName }}
                </div>
              </div>

              <div>
                <div class="step-list-text">メール配信数</div>
                <div class="step-list-value">
                  {{ numberFormat(step.sendCount) }}
                </div>
              </div>
            </div>
          </div>

          <!-- time -->
          <div v-if="delivery.steps.length > index + 1" class="trigger-divider py-8 mt-4">
            <div class="trigger-center-line"></div>
            <div class="trigger-time">
              <span v-if="delivery.steps[index + 1].isInstantDeliveryStep">即時</span>
              <span v-else>
                {{ delivery.steps[index + 1].deliveryDaysLater }}日後 {{ delivery.steps[index + 1].deliveryTime }}:00
              </span>
            </div>
          </div>
        </div>
      </div>
    </v-expand-transition>
  </v-sheet>
</template>
<style scoped lang="scss">
  .header-content {
    cursor: pointer;
    &:hover {
      background-color: rgb(var(--v-theme-monochrome-07)) !important;
    }
  }
  .step-content {
    background-color: rgb(var(--v-theme-monochrome-04));
  }
  .step-list {
    display: flex;
    gap: 50px;
    width: fit-content;
    margin-left: auto;
    margin-right: auto;
    flex-wrap: wrap;
    justify-content: center;
    background-color: rgb(var(--v-theme-monochrome-05));
    padding: 20px;
    border-radius: 5px;

    .step-list-text {
      font-size: 14px;
      color: white;
      font-weight: bold;
      text-align: center;
      margin-bottom: 4px;
    }
    .step-list-value {
      font-size: 14px;
      color: white;
      text-align: center;
      max-width: 300px;
    }
  }
  .trigger-divider {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 104px;

    .trigger-center-line {
      position: absolute;
      top: 0;
      left: 50%;
      transform: translateX(-50%);
      height: 100%;
      width: 1px;
      background-color: rgba(var(--v-theme-monochrome-02));
      z-index: 0;
    }

    .trigger-time {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background-color: rgb(var(--v-theme-monochrome-04));
      padding: 8px;
      color: white;
      font-size: 14px;
      font-weight: bold;
      z-index: 1;
    }
  }
  .triangle {
    margin-bottom: 2px;
    cursor: pointer;
    transition: transform 0.3s ease;
    transform: rotate(90deg);
    &.down {
      transform: rotate(180deg);
    }
  }
  .step-list-wrapper {
    width: fit-content;
    margin-left: auto;
    margin-right: auto;
  }
</style>
